import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import {
  MapPin, Phone, Clock, ChevronDown, ChevronUp, RefreshCw,
  XCircle, PlusCircle, Store,
} from "lucide-react";
import type { DbProduct } from "@/hooks/useRestaurant";

type Branch = {
  id: string;
  name: string;
  name_ar: string | null;
  address: string | null;
  phone: string | null;
  working_hours: string | null;
  is_open: boolean;
  is_busy: boolean;
  estimated_prep_time: number | null;
};

type Props = {
  restaurantId: string | undefined;
  menuItems: DbProduct[];
};

export default function AdminBranches({ restaurantId, menuItems }: Props) {
  const { toast } = useToast();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedBranch, setExpandedBranch] = useState<string | null>(null);
  const [unavailableMap, setUnavailableMap] = useState<Record<string, string[]>>({});
  const [savingItem, setSavingItem] = useState<string | null>(null);

  const fetchBranches = async () => {
    if (!restaurantId) return;
    setLoading(true);
    const { data } = await supabase
      .from("branches")
      .select("id, name, name_ar, address, phone, working_hours, is_open, is_busy, estimated_prep_time")
      .eq("restaurant_id", restaurantId)
      .order("sort_order");
    setBranches(data || []);
    setLoading(false);
  };

  const fetchUnavailableItems = async (branchId: string) => {
    const { data } = await supabase
      .from("branch_unavailable_items")
      .select("product_id")
      .eq("branch_id", branchId);
    setUnavailableMap((prev) => ({
      ...prev,
      [branchId]: (data || []).map((d) => d.product_id),
    }));
  };

  useEffect(() => { fetchBranches(); }, [restaurantId]);

  const handleToggleBranch = (branchId: string) => {
    if (expandedBranch === branchId) {
      setExpandedBranch(null);
    } else {
      setExpandedBranch(branchId);
      if (!unavailableMap[branchId]) fetchUnavailableItems(branchId);
    }
  };

  const handleToggleOpen = async (branch: Branch) => {
    const { error } = await supabase.from("branches").update({ is_open: !branch.is_open }).eq("id", branch.id);
    if (!error) {
      setBranches((prev) => prev.map((b) => (b.id === branch.id ? { ...b, is_open: !b.is_open } : b)));
      toast({ title: `Branch ${!branch.is_open ? "opened" : "closed"}` });
    }
  };

  const handleToggleBusy = async (branch: Branch) => {
    const { error } = await supabase.from("branches").update({ is_busy: !branch.is_busy }).eq("id", branch.id);
    if (!error) {
      setBranches((prev) => prev.map((b) => (b.id === branch.id ? { ...b, is_busy: !b.is_busy } : b)));
      toast({ title: `Branch ${!branch.is_busy ? "marked busy" : "no longer busy"}` });
    }
  };

  const handleMarkUnavailable = async (branchId: string, productId: string) => {
    setSavingItem(productId);
    const { error } = await supabase
      .from("branch_unavailable_items")
      .insert({ branch_id: branchId, product_id: productId });
    if (!error) {
      setUnavailableMap((prev) => ({ ...prev, [branchId]: [...(prev[branchId] || []), productId] }));
      toast({ title: "Item marked unavailable" });
    }
    setSavingItem(null);
  };

  const handleMarkAvailable = async (branchId: string, productId: string) => {
    setSavingItem(productId);
    const { error } = await supabase
      .from("branch_unavailable_items")
      .delete()
      .eq("branch_id", branchId)
      .eq("product_id", productId);
    if (!error) {
      setUnavailableMap((prev) => ({ ...prev, [branchId]: (prev[branchId] || []).filter((id) => id !== productId) }));
      toast({ title: "Item marked available" });
    }
    setSavingItem(null);
  };

  if (loading) {
    return (
      <Card>
        <CardHeader><CardTitle className="text-lg">Branches</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {[1, 2, 3].map((i) => (<Skeleton key={i} className="h-20 w-full rounded-lg" />))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg flex items-center gap-2">
          <Store className="w-5 h-5" /> Branches ({branches.length})
        </CardTitle>
        <Button variant="outline" size="sm" onClick={fetchBranches}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </CardHeader>
      <CardContent>
        {branches.length === 0 ? (
          <p className="text-muted-foreground text-sm">No branches found.</p>
        ) : (
          <div className="space-y-3">
            {branches.map((branch) => {
              const isExpanded = expandedBranch === branch.id;
              const unavailable = unavailableMap[branch.id] || [];
              return (
                <div key={branch.id} className="border rounded-lg overflow-hidden">
                  <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/30 transition-colors" onClick={() => handleToggleBranch(branch.id)}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold">{branch.name}</span>
                        {branch.name_ar && <span className="text-sm text-muted-foreground" dir="rtl">{branch.name_ar}</span>}
                        <Badge variant={branch.is_open ? "default" : "destructive"} className="text-xs">{branch.is_open ? "Open" : "Closed"}</Badge>
                        {branch.is_busy && <Badge variant="secondary" className="text-xs">Busy</Badge>}
                        {unavailable.length > 0 && <Badge variant="outline" className="text-xs">{unavailable.length} unavailable</Badge>}
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground flex-wrap">
                        {branch.address && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {branch.address}</span>}
                        {branch.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {branch.phone}</span>}
                        {branch.working_hours && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {branch.working_hours}</span>}
                      </div>
                    </div>
                    {isExpanded ? <ChevronUp className="w-5 h-5 text-muted-foreground shrink-0" /> : <ChevronDown className="w-5 h-5 text-muted-foreground shrink-0" />}
                  </div>

                  {isExpanded && (
                    <div className="border-t p-4 space-y-4 bg-muted/10">
                      <div className="flex items-center gap-6 flex-wrap">
                        <div className="flex items-center gap-2">
                          <Switch checked={branch.is_open} onCheckedChange={() => handleToggleOpen(branch)} />
                          <span className="text-sm font-medium">Open</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch checked={branch.is_busy} onCheckedChange={() => handleToggleBusy(branch)} />
                          <span className="text-sm font-medium">Busy</span>
                        </div>
                        {branch.estimated_prep_time && <span className="text-sm text-muted-foreground">~{branch.estimated_prep_time} min prep time</span>}
                      </div>

                      <div>
                        <h4 className="text-sm font-semibold mb-3">Product Availability ({menuItems.length} items)</h4>
                        {menuItems.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No products to manage.</p>
                        ) : (
                          <div className="space-y-1 max-h-[400px] overflow-y-auto">
                            {menuItems.map((item) => {
                              const isUnavailable = unavailable.includes(item.id);
                              const isSaving = savingItem === item.id;
                              return (
                                <div key={item.id} className={`flex items-center justify-between p-2.5 rounded-md border transition-colors ${isUnavailable ? "bg-destructive/5 border-destructive/20" : "bg-card border-border"}`}>
                                  <div className="flex items-center gap-3 min-w-0">
                                    {item.image_url && <img src={item.image_url} alt={item.name} className="w-8 h-8 rounded object-cover shrink-0" />}
                                    <div className="min-w-0">
                                      <span className={`text-sm font-medium ${isUnavailable ? "line-through text-muted-foreground" : ""}`}>{item.name}</span>
                                      <span className="text-xs text-muted-foreground ml-2">${Number(item.price).toFixed(2)}</span>
                                    </div>
                                  </div>
                                  <Button
                                    variant={isUnavailable ? "outline" : "ghost"}
                                    size="sm"
                                    disabled={isSaving}
                                    onClick={() => isUnavailable ? handleMarkAvailable(branch.id, item.id) : handleMarkUnavailable(branch.id, item.id)}
                                    className={`shrink-0 ${isUnavailable ? "text-primary hover:text-primary" : "text-destructive hover:text-destructive"}`}
                                  >
                                    {isUnavailable ? (<><PlusCircle className="w-4 h-4 mr-1" /> Restore</>) : (<><XCircle className="w-4 h-4 mr-1" /> Mark Sold Out</>)}
                                  </Button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
